import React, { useState } from 'react';
import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import audienceviewlogo from '../../audienceviewlogo.png';
import uoftlogo from '../../uoftlogo.png';
import strataprimelogo from '../../strataprimelogo.png';
import { Image } from 'react-bootstrap';
import microsoftlogo from '../../microsoftlogo.png';
import oktalogo from '../../oktalogo.png';
import './ExperienceTimeLine.css';

function ExperienceTimeLine(props) {
    const [expandedJobs, setExpandedJobs] = useState({});

    const toggleJobExpansion = (jobId) => {
        setExpandedJobs(prev => ({
            ...prev,
            [jobId]: !prev[jobId]
        }));
    };
    const experiences = [
        {
            id: 'okta',
            logo: oktalogo,
            company: 'Okta',
            title: 'Senior Security Engineer',
            period: 'April 2024 - Present',
            product: 'All products',
            achievements: [
                'Conducting design reviews, code reviews, and penetration tests for Okta\'s products and new feature releases, covering web, desktop, mobile, and AI-based applications',
                'As the threat modeling lead, I streamline Okta\'s threat modeling process by developing standards, methodologies, educational materials, and other process improvements',
                'Contributing agentic AI capabilities for threat modeling and security review automation by developing MCP servers and Claude Code integrations',
                'Performing triage and validation of vulnerabilities reported internally or through Okta\'s bug bounty program',
                'Performing in-depth security reviews focused on areas such as cryptography and XSS. This includes identifying vulnerabilities, developing improved Semgrep detection rules, and creating educational materials',
                'Partnering with Security Champions to drive security education and facilitate security transformations across the company'
            ]
        },
        {
            id: 'microsoft',
            logo: microsoftlogo,
            company: 'Microsoft',
            title: 'Software Engineer II',
            period: 'August 2021 - April 2024',
            product: 'Microsoft Whiteboard',
            achievements: [
                'Engaged with stakeholders to implement core features for Surface Hub and Teams Rooms devices using React, TypeScript, and C#',
                'Improved security posture by developing STRIDE threat models, performing security reviews, and mitigating vulnerabilities; conducted a presentation to approximately 40 colleagues',
                'Implemented bug fixes, hotfixes, and unit tests for user interfaces and API service functionalities',
                'As an on-call engineer, engaged with clients and other teams to investigate and mitigate high-priority incidents',
                'Improved incident response by creating troubleshooting guides for critical failures and implementing telemetry dashboards for monitoring'
            ]
        },
        {
            id: 'audienceview',
            logo: audienceviewlogo,
            company: 'AudienceView',
            title: 'Front-End Software Engineer',
            period: 'Nov 2019 - August 2021',
            product: 'AudienceView Unlimited',
            achievements: [
                'Developed event management UI using JavaScript and Bootstrap to aid ticket sales across 1000+ venues per year',
                'Co-led feature developments for WCAG web accessibility guidelines, increasing user accessibility by 40%',
                'Created factory patterns for tables and modal windows that provide responsive, accessible and cross-browser capabilities; increased productivity by approximately 20%',
                'Led application security improvements by developing scalable solutions against OWASP Top 10 vulnerabilities, optimizing ZAP scans, and improving secure coding standards; mitigated roughly 60% of known security risks'
            ]
        },
        {
            id: 'uoft-ta',
            logo: uoftlogo,
            company: 'University of Toronto',
            title: 'Undergraduate Teaching Assistant',
            period: 'January 2019 - January 2020',
            product: '',
            achievements: [
                'Taught Introduction to Programming (CSC108), Introduction to Computer Science (CSC148), and Theory of Computation (CSC236) to approximately 40 students in tutorial sessions',
                'Produced questions used in tutorial sessions, assignments, and examinations',
                'Helped students succeed in their courses by teaching learning techniques and problem-solving strategies'
            ]
        },
        {
            id: 'strataprime',
            logo: strataprimelogo,
            company: 'StrataPrime',
            title: 'Full-Stack Software Developer',
            period: 'May 2018 - August 2018',
            product: 'Availability Tracker',
            achievements: [
                'Solely developed a web application using Google Apps Script that allows managers to keep track and be in control of employee availability within the G Suite environment',
                'Designed Cloud SQL database, user interfaces, and general operations'
            ]
        },
        {
            id: 'uoft-research',
            logo: uoftlogo,
            company: 'University of Toronto',
            title: 'Undergraduate Research Chemist',
            period: 'May 2016 - October 2016',
            product: '',
            achievements: [
                'As a Research Opportunity Program student, worked with a supervisor to design and synthesize a library of derivative molecules with desirable biological efficacy against ROS1-fusion related disease'
            ]
        }
    ];

    return (
        <section className="experience-timeline" aria-labelledby="experience-heading">
            {/* Horizontal Timeline Summary */}
            <div className="horizontal-timeline-container" aria-label="Career timeline overview">
                <div className="horizontal-timeline">
                    {[...experiences].reverse().map((exp, index) => (
                        <div key={exp.id} className="horizontal-timeline-item">
                            <button 
                                className="timeline-logo-wrapper"
                                onClick={() => {
                                    const element = document.querySelector(`.${exp.id}-section`);
                                    if (element) {
                                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    }
                                }}
                                aria-label={`Navigate to ${exp.company} job description`}
                                type="button"
                            >
                                <img 
                                    src={exp.logo} 
                                    alt={`${exp.company} logo`}
                                    className="timeline-logo"
                                    loading="lazy"
                                />
                                <div className="timeline-company-label">{exp.company}</div>
                            </button>
                            {index < experiences.length - 1 && <div className="timeline-connector-horizontal" aria-hidden="true"></div>}
                        </div>
                    ))}
                </div>
            </div>

            <Timeline className="p-0" aria-label="Professional experience timeline" role="list">
                {experiences.map((exp, index) => (
                    <TimelineItem 
                        key={exp.id} 
                        className={`experience-item ${exp.id}-section`}
                        aria-label={`${exp.title} at ${exp.company}`}
                    >
                        <TimelineSeparator>
                            <TimelineDot 
                                className="timeline-dot"
                                aria-hidden="true"
                            />
                            {index < experiences.length - 1 && <TimelineConnector className="timeline-connector" />}
                        </TimelineSeparator>
                        <TimelineContent>
                            <article 
                                className={`timeline-jobdesc ${expandedJobs[exp.id] ? 'expanded' : 'collapsed'}`} 
                                role="region" 
                                aria-label={`${exp.title} role details`}
                            >
                                <button
                                    className="job-toggle-btn"
                                    onClick={() => toggleJobExpansion(exp.id)}
                                    aria-expanded={expandedJobs[exp.id] || false}
                                    aria-controls={`job-details-${exp.id}`}
                                >
                                    <Image 
                                        src={exp.logo} 
                                        alt={`${exp.company} logo`}
                                        className="companyimg"
                                        loading="lazy"
                                    />
                                    
                                    <h3 className="job-title">{exp.title}</h3>
                                    
                                    <div className="toggle-icon">
                                        <span className="toggle-chevron">▼</span>
                                    </div>
                                </button>

                                <div 
                                    id={`job-details-${exp.id}`}
                                    className="job-details-container"
                                >
                                    <div className="job-metadata">
                                        <p className="company-name">
                                            <strong>{exp.company}</strong>
                                        </p>
                                        <p className="employment-period">
                                            <time>{exp.period}</time>
                                        </p>
                                        {exp.product && (
                                            <p className="product-info">
                                                <span className="label">Product:</span> {exp.product}
                                            </p>
                                        )}
                                    </div>
                                    
                                    <ul className="job-achievements" aria-label="Key responsibilities and achievements">
                                        {exp.achievements.map((achievement, i) => (
                                            <li key={i} className="achievement-item">
                                                {achievement}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </article>
                        </TimelineContent>
                    </TimelineItem>
                ))}
            </Timeline>
        </section>
    );
}

export default ExperienceTimeLine;
